/**
 * Stats Bar Component - Horizontal rectangular metrics display
 * Replaces orbital metrics with sleek horizontal bars
 */

import React from 'react';
import { formatCost } from '@/utils/format';

interface StatsBarProps {
  metrics: {
    activeExecutions: number;
    totalCost: number;
    successRate: number;
    avgQuality: number;
    runningCount?: number;
    completedCount?: number;
  };
  className?: string;
}

const StatsBar: React.FC<StatsBarProps> = ({ metrics, className = '' }) => {
  const stats = [
    {
      label: 'ACTIVE',
      value: metrics.activeExecutions,
      unit: '',
      color: 'cyan',
      glow: 'rgba(6, 182, 212',
      gradient: 'from-cyan-500/20 to-cyan-600/10',
      borderColor: 'border-cyan-500/30',
      description: `${metrics.runningCount || 0} running`
    },
    {
      label: 'COST',
      value: formatCost(metrics.totalCost),
      unit: '',
      color: 'success',
      glow: 'rgba(16, 185, 129',
      gradient: 'from-emerald-500/20 to-emerald-600/10',
      borderColor: 'border-emerald-500/30',
      description: 'Total incurred'
    },
    {
      label: 'SUCCESS',
      value: Math.round(metrics.successRate),
      unit: '%',
      color: metrics.successRate > 80 ? 'success' : metrics.successRate > 50 ? 'warning' : 'error',
      glow: metrics.successRate > 80 ? 'rgba(16, 185, 129' : metrics.successRate > 50 ? 'rgba(245, 158, 11' : 'rgba(239, 68, 68',
      gradient: metrics.successRate > 80 ? 'from-emerald-500/20 to-emerald-600/10' : metrics.successRate > 50 ? 'from-amber-500/20 to-amber-600/10' : 'from-red-500/20 to-red-600/10',
      borderColor: metrics.successRate > 80 ? 'border-emerald-500/30' : metrics.successRate > 50 ? 'border-amber-500/30' : 'border-red-500/30',
      description: `${metrics.completedCount || 0} completed`
    },
    {
      label: 'QUALITY',
      value: Math.round(metrics.avgQuality),
      unit: '/100',
      color: metrics.avgQuality > 80 ? 'purple' : metrics.avgQuality > 60 ? 'blue' : 'warning',
      glow: metrics.avgQuality > 80 ? 'rgba(168, 85, 247' : metrics.avgQuality > 60 ? 'rgba(59, 130, 246' : 'rgba(245, 158, 11',
      gradient: metrics.avgQuality > 80 ? 'from-purple-500/20 to-purple-600/10' : metrics.avgQuality > 60 ? 'from-blue-500/20 to-blue-600/10' : 'from-amber-500/20 to-amber-600/10',
      borderColor: metrics.avgQuality > 80 ? 'border-purple-500/30' : metrics.avgQuality > 60 ? 'border-blue-500/30' : 'border-amber-500/30',
      description: 'Average score'
    }
  ];

  return (
    <div className={`w-full ${className}`}>
      <div className="grid grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="relative group"
            style={{
              animation: `fadeIn ${0.5 + index * 0.1}s ease-out`
            }}
          >
            {/* Background gradient */}
            <div className={`
              absolute inset-0 rounded-lg bg-gradient-to-b ${stat.gradient}
              opacity-50 group-hover:opacity-70 transition-opacity duration-300
            `} />
            
            {/* Main container */}
            <div className={`
              relative h-16 rounded-lg
              bg-black/40 backdrop-blur-md
              border ${stat.borderColor}
              overflow-hidden
              transition-all duration-300
              hover:shadow-lg
            `}
            style={{
              boxShadow: `
                0 4px 20px ${stat.glow}, 0.1),
                inset 0 1px 0 rgba(255, 255, 255, 0.1),
                inset 0 -1px 0 rgba(0, 0, 0, 0.3)
              `
            }}>
              {/* Animated border glow on hover */}
              <div className={`
                absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100
                transition-opacity duration-300
              `}
              style={{
                background: `radial-gradient(600px circle at var(--mouse-x) var(--mouse-y), ${stat.glow}, 0.15), transparent)`,
                borderRadius: 'inherit'
              }} />

              {/* Content */}
              <div className="relative h-full flex flex-col justify-center px-3">
                {/* Label */}
                <div className="text-xs font-medium tracking-wider text-white/70 uppercase mb-1 text-right">
                  {stat.label}
                </div>
                
                {/* Value display */}
                <div className="flex items-baseline text-left">
                  <span 
                    className={`text-xl font-mono font-bold led-readout`}
                    style={{
                      color: `${stat.glow}, 1)`,
                      textShadow: `
                        0 0 10px ${stat.glow}, 0.8),
                        0 0 20px ${stat.glow}, 0.6),
                        0 0 30px ${stat.glow}, 0.4)
                      `
                    }}
                  >
                    {stat.value}
                  </span>
                  {stat.unit && (
                    <span 
                      className="text-sm ml-1 opacity-70 font-mono"
                      style={{
                        color: `${stat.glow}, 0.9)`,
                        textShadow: `0 0 10px ${stat.glow}, 0.5)`
                      }}
                    >
                      {stat.unit}
                    </span>
                  )}
                </div>
              </div>

              {/* Animated pulse effect */}
              <div className={`
                absolute bottom-0 left-0 right-0 h-px
                bg-gradient-to-r from-transparent via-current to-transparent
                opacity-50 animate-pulse
              `}
              style={{
                color: `${stat.glow}, 1)`
              }} />

              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-4 h-4">
                <div className={`absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent to-current opacity-30`}
                  style={{ color: `${stat.glow}, 1)` }} />
                <div className={`absolute top-0 left-0 h-full w-px bg-gradient-to-b from-transparent to-current opacity-30`}
                  style={{ color: `${stat.glow}, 1)` }} />
              </div>
              <div className="absolute top-0 right-0 w-4 h-4">
                <div className={`absolute top-0 right-0 w-full h-px bg-gradient-to-l from-transparent to-current opacity-30`}
                  style={{ color: `${stat.glow}, 1)` }} />
                <div className={`absolute top-0 right-0 h-full w-px bg-gradient-to-b from-transparent to-current opacity-30`}
                  style={{ color: `${stat.glow}, 1)` }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatsBar;